import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, MapPin, Phone, Plus, Store, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { ModalOverlay } from "@/components/ModalOverlay";
import { PaginatedList } from "@/components/PaginatedList";
import { usePartnerScope } from "@/components/partner-scope-context";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { usePaginatedList } from "@/lib/pagination";
import { hasWriteOn, isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import { validateEmail, validatePhone } from "@/lib/validation";
import type { Organization, Partner } from "@tilt/admin-sdk";

export function OrganizationsPage(): JSX.Element {
  usePageTitle("Organizations");
  const queryClient = useQueryClient();
  const { scope } = usePartnerScope();
  // Deep-linkable state: /organizations?partner=<id>&create=1 opens the
  // create dialog with that partner pre-filtered. Used by the
  // "New organization" CTA on /partners/:id so the user doesn't have
  // to re-pick the partner they just drilled into.
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(searchParams.get("create") === "1");
  const [showDeleted, setShowDeleted] = useState(false);
  const [partnerFilter, setPartnerFilter] = useState<string>(searchParams.get("partner") ?? "");

  useEffect(() => {
    // Strip the query params after first paint so a refresh of the page
    // doesn't re-open the dialog.
    if (searchParams.has("create") || searchParams.has("partner")) {
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      next.delete("partner");
      setSearchParams(next, { replace: true });
    }
    // Intentionally empty - only strip on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After V0012 every customer_org row got merged into organization_tbl
  // and their former parent (a partner after V0012) is what now holds
  // orgs. So the "pick a parent for this org" list is the set of
  // partner partners in scope, not customer_org rows. Still include
  // legacy customer_org rows so operators on a half-migrated env can
  // see them too.
  const partners = useQuery({
    queryKey: ["partners", "for-orgs", scope.id],
    queryFn: () => apiClient.listPartners(),
    retry: false,
  });

  const parentPartners = useMemo(
    () =>
      (partners.data?.partners ?? []).filter((t) => !t.is_deleted && t.partner_type !== "platform"),
    [partners.data],
  );

  const orgsList = usePaginatedList({
    queryKey: ["organizations", scope.id, { showDeleted, partnerFilter }],
    fetcher: ({ limit, offset, q }) =>
      apiClient
        .listOrganizations({
          limit,
          offset,
          ...(q ? { q } : {}),
          scope_path: scope.path,
          include_deleted: showDeleted,
          ...(partnerFilter ? { partner_id: partnerFilter } : {}),
        })
        .then((res) => ({
          items: res.organizations,
          total: res.total,
          limit: res.limit,
          offset: res.offset,
          has_more: res.has_more,
        })),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["organizations"] });
  };

  // "New organization" creates under a parent partner - require_write
  // on that partner's path. Hide the button when the caller has no
  // write-capable grant on ANY visible partner in their scope. Super-
  // admins always see it.
  const profile = useRoleProfile();
  const canCreate =
    parentPartners.some((t) => hasWriteOn(profile, t.path)) || isSuperAdmin(profile);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Organizations</h1>
          <p className="text-sm text-muted-foreground">
            The customers you serve. Each organization runs one or more locations.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex flex-shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            New organization
          </button>
        )}
      </div>

      <PaginatedList
        state={orgsList}
        itemNoun="organizations"
        searchPlaceholder="Search by name, partner name, or path…"
        filters={
          <>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Partner
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                <option value="">All in scope</option>
                {parentPartners.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Show deleted
            </label>
          </>
        }
        emptyState={
          parentPartners.length === 0 ? (
            <EmptyState
              icon={Store}
              title="No partners in your scope"
              description="Organizations sit under a partner. Create a partner on the Partners page, then come back here."
              cta={{ label: "Go to Partners", href: "/partners" }}
            />
          ) : (
            <EmptyState
              icon={Store}
              title="No organizations yet"
              description={
                canCreate
                  ? "Organizations sit between a partner and its locations. Click “New organization” above to create one."
                  : "No organizations in your scope yet. Ask an admin with write access to create one."
              }
              {...(canCreate
                ? { cta: { label: "New organization", onClick: () => setCreateOpen(true) } }
                : {})}
            />
          )
        }
      >
        {orgsList.query.isError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {orgsList.query.error instanceof Error
              ? orgsList.query.error.message
              : "Failed to load organizations"}
          </div>
        )}
        {orgsList.items.length > 0 && (
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Partner</th>
                  <th className="px-4 py-2.5 text-right">Locations</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgsList.items.map((o) => (
                  <OrganizationRow key={o.id} org={o} onMutated={invalidate} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PaginatedList>

      {createOpen && (
        <CreateDialog
          partners={parentPartners}
          defaultPartnerId={
            // User-selected filter wins; otherwise pre-fill the active
            // scope so a super_admin working "inside Finterra" doesn't
            // have to re-pick the parent on every new-org click.
            partnerFilter ||
            (parentPartners.some((t) => t.id === scope.id) ? scope.id : "") ||
            parentPartners[0]?.id ||
            ""
          }
          onClose={() => setCreateOpen(false)}
          onMutated={() => {
            setCreateOpen(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function OrganizationRow(props: { org: Organization; onMutated: () => void }): JSX.Element {
  const { org, onMutated } = props;
  const [error, setError] = useState<string | null>(null);
  const profile = useRoleProfile();
  const canDelete = hasWriteOn(profile, org.partner_path);
  const softDelete = useMutation({
    mutationFn: () => apiClient.deleteOrganization(org.id),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <>
      <tr className="border-b last:border-b-0 hover:bg-muted/30">
        <td className="px-4 py-2.5 align-middle">
          <div className="flex items-center gap-2">
            <Store className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            {org.is_deleted ? (
              <span className="font-medium">{org.name}</span>
            ) : (
              <Link
                to={`/organizations/${org.id}`}
                className="font-medium hover:text-primary hover:underline"
              >
                {org.name}
              </Link>
            )}
            {org.is_deleted && (
              <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
                deleted
              </span>
            )}
          </div>
          {(org.contact_email || org.contact_phone || org.address) && (
            <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
              {org.address && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" aria-hidden="true" />
                  {org.address}
                </span>
              )}
              {org.contact_email && (
                <span className="inline-flex items-center gap-1">
                  <Mail className="h-3 w-3" aria-hidden="true" />
                  {org.contact_email}
                </span>
              )}
              {org.contact_phone && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3 w-3" aria-hidden="true" />
                  {org.contact_phone}
                </span>
              )}
            </div>
          )}
        </td>
        <td className="break-all px-4 py-2.5 align-middle font-mono text-xs text-muted-foreground">
          {org.partner_path ?? "-"}
        </td>
        <td className="px-4 py-2.5 text-right align-middle tabular-nums text-muted-foreground">
          {org.location_count}
        </td>
        <td className="px-4 py-2.5 align-middle">
          <div className="flex items-center justify-end gap-2">
            {!org.is_deleted && canDelete && (
              <button
                type="button"
                disabled={softDelete.isPending}
                onClick={() => {
                  const warn =
                    org.location_count > 0
                      ? `Delete "${org.name}"? Will fail - ${org.location_count} active location(s). Delete those first.`
                      : `Soft-delete "${org.name}"?`;
                  if (!confirm(warn)) return;
                  softDelete.mutate();
                }}
                aria-label={`Delete ${org.name}`}
                className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            )}
          </div>
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={4} className="px-4 py-2">
            <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
          </td>
        </tr>
      )}
    </>
  );
}

function CreateDialog(props: {
  partners: Partner[];
  defaultPartnerId: string;
  onClose: () => void;
  onMutated: () => void;
}): JSX.Element {
  const [partnerId, setPartnerId] = useState(props.defaultPartnerId);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const emailCheck = validateEmail(contactEmail);
  const phoneCheck = validatePhone(contactPhone);

  const create = useMutation({
    mutationFn: () => {
      if (!emailCheck.ok) throw new Error(emailCheck.error);
      if (!phoneCheck.ok) throw new Error(phoneCheck.error);
      return apiClient.createOrganization({
        partner_id: partnerId,
        name: name.trim(),
        ...(address.trim() ? { address: address.trim() } : {}),
        ...(contactEmail.trim() ? { contact_email: contactEmail.trim() } : {}),
        ...(contactPhone.trim() ? { contact_phone: contactPhone.trim() } : {}),
      });
    },
    onSuccess: () => {
      setError(null);
      props.onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  // Empty-partners fallback - surface a route to /partners instead of
  // rendering a form the user can't submit.
  if (props.partners.length === 0) {
    return (
      <ModalOverlay ariaLabel="Create organization" onClose={props.onClose}>
        <div className="w-full max-w-md space-y-3 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl">
          <h2 className="text-sm font-semibold">New organization</h2>
          <EmptyState
            icon={Store}
            title="No partners in your scope"
            description="An organization sits under a partner. Create a partner on the Partners page first, then come back here."
            cta={{ label: "Go to Partners", href: "/partners" }}
            secondaryCta={{ label: "Close", onClick: props.onClose }}
          />
        </div>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay ariaLabel="Create organization" onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="w-full max-w-md space-y-3 rounded-t-xl border bg-card p-4 shadow-lg sm:rounded-xl sm:p-5"
      >
        <h2 className="text-sm font-semibold">New organization</h2>

        <div>
          <label
            htmlFor="o-partner"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Partner
          </label>
          <select
            id="o-partner"
            required
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            {props.partners.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="o-name" className="mb-1 block text-xs font-medium text-muted-foreground">
            Name
          </label>
          <input
            id="o-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="Acme Downtown Org"
          />
        </div>

        <div>
          <label htmlFor="o-addr" className="mb-1 block text-xs font-medium text-muted-foreground">
            Address <span className="text-muted-foreground/80">(optional)</span>
          </label>
          <input
            id="o-addr"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="123 Main St, Denver CO"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="o-email"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Contact email
            </label>
            <input
              id="o-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              aria-invalid={!emailCheck.ok}
              className={cn(
                "w-full rounded-md border bg-background px-3 py-2 text-sm",
                !emailCheck.ok && "border-destructive",
              )}
              placeholder="ops@acme.test"
            />
            {!emailCheck.ok && (
              <p className="mt-1 text-[11px] text-destructive">{emailCheck.error}</p>
            )}
          </div>
          <div>
            <label
              htmlFor="o-phone"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Contact phone
            </label>
            <input
              id="o-phone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              aria-invalid={!phoneCheck.ok}
              className={cn(
                "w-full rounded-md border bg-background px-3 py-2 text-sm",
                !phoneCheck.ok && "border-destructive",
              )}
              placeholder="+1 555-0100"
            />
            {!phoneCheck.ok && (
              <p className="mt-1 text-[11px] text-destructive">{phoneCheck.error}</p>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending || !partnerId || !name.trim()}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {create.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}
